import { useFlag } from '@reflag/react-sdk';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';

import type * as api from '@gitbook/api-client';
import { Icon } from '@gitbook/spine-icons';
import {
    StepProgressCard,
    StepProgressCardDismissButton,
    StepProgressCardFooter,
    StepProgressCardHeader,
    StepProgressCardProgress,
    StepProgressCardSteps,
    type StepProgressCardStep,
} from '@gitbook/spine-react';
import { SITE_SETUP_STEPS } from '@services/content-agents/browser';

import {
    useAgentEnabled,
    useSiteSetupConversation,
    useSiteSetupStepStatuses,
} from '../../Components';
import { apiQuery, useLoggedInUser } from '../../state';

/**
 * Whether a user dismissed the setup card of a site, in this browser. The card only lives until the
 * site is published, and one user may want it while another does not.
 */
const siteSetupCardDismissedAtom = atomFamily((key: string) =>
    atomWithStorage(`siteSetupCardDismissed__${JSON.stringify(key)}`, false, undefined, {
        getOnInit: true,
    })
);

/**
 * The site's setup steps, until the site is published. Each step opens the site's setup
 * conversation in the agent screen and asks the agent to work on it.
 */
export function SiteSetupCard(props: { organizationId: string; site: api.Site }) {
    const { organizationId, site } = props;

    const user = useLoggedInUser();
    const [dismissed, setDismissed] = useAtom(siteSetupCardDismissedAtom(`${user.uid}:${site.id}`));
    const siteAISetupFlag = useFlag('SITE_AI_SETUP');
    const agentEnabled = useAgentEnabled();
    const enabled = siteAISetupFlag.isEnabled && agentEnabled && !dismissed;

    const { data: setup } = useQuery({
        ...apiQuery('getSiteSetup', enabled ? { organizationId, siteId: site.id } : skipToken),
        throwOnError: false,
    });
    const setupConversation = useSiteSetupConversation({
        organizationId,
        siteId: enabled ? site.id : undefined,
    });
    const statuses = useSiteSetupStepStatuses(site.id);

    // No steps once the site is published, which ends the setup.
    if (!setup?.steps.length || !setupConversation) {
        return null;
    }

    // Done from the site's data, skipped or started from the setup conversation in this browser.
    const isOpen = (step: api.SiteSetup['steps'][number]) =>
        !step.completed && statuses[step.id] !== 'skipped';
    const nextIndex = setup.steps.findIndex(isOpen);
    const steps = setup.steps.map((step, index): StepProgressCardStep => {
        const { title } = SITE_SETUP_STEPS[step.id];
        return {
            id: step.id,
            title,
            state: step.completed
                ? 'done'
                : !isOpen(step)
                  ? 'skipped'
                  : index === nextIndex
                    ? statuses[step.id] === 'current'
                        ? 'current'
                        : 'next'
                    : 'pending',
            href: {
                screen: {
                    type: 'agent',
                    organization: organizationId,
                    site: site.id,
                    conversation: setupConversation.id,
                },
            },
            onPress: () => setupConversation.sendPromptOnOpen(`Let's work on "${title}"`),
        };
    });

    return (
        <StepProgressCard steps={steps}>
            <StepProgressCardHeader icon={Icon.AIAgent} title="Setup">
                <StepProgressCardDismissButton
                    dialogTitle="Dismiss setup?"
                    dialogDescription="The setup card will leave the sidebar for this site. You can still continue the setup guide from the agent."
                    onDismiss={() => setDismissed(true)}
                />
            </StepProgressCardHeader>
            <StepProgressCardProgress />
            <StepProgressCardSteps />
            <StepProgressCardFooter />
        </StepProgressCard>
    );
}
